import React, {useState, useLayoutEffect, useCallback} from 'react';
import {Range} from 'slate';
import {useSlateSelector} from 'slate-react';

interface FloatingCommentButtonProps {
  insertComment: () => void;
}

const computeButtonPosition = (): {top: number; left: number} | null => {
  const domR = window.getSelection()?.getRangeAt(0);
  if (!domR) return null;

  const rect = domR.getBoundingClientRect();
  const p = document
    .elementFromPoint(rect.left, rect.top)
    ?.closest('.paginated-page');
  const eP = document.querySelector('.editor-panel');
  if (!p || !(eP instanceof HTMLElement)) return null;

  const pR = p.getBoundingClientRect();
  const pRect = eP.getBoundingClientRect();
  const BTN = 32;
  const GAP = 15;
  const MARGIN = 8;
  let left = pR.right - pRect.left + eP.scrollLeft + GAP;
  let top =
    rect.top - pRect.top + eP.scrollTop + rect.height / 2 - BTN / 2;

  const minL = eP.scrollLeft + MARGIN;
  const maxL = eP.scrollLeft + eP.clientWidth - BTN - MARGIN;
  if (maxL >= minL) {
    if (left > maxL) {
      const flip = rect.left - pRect.left + eP.scrollLeft - BTN - GAP;
      left = flip >= minL ? flip : Math.min(Math.max(left, minL), maxL);
    } else {
      left = Math.min(Math.max(left, minL), maxL);
    }
  }

  const minT = eP.scrollTop + MARGIN;
  const maxT = eP.scrollTop + eP.clientHeight - BTN - MARGIN;
  top =
    maxT >= minT
      ? Math.min(Math.max(top, minT), maxT)
      : Math.max(minT, Math.min(top, eP.scrollTop + eP.clientHeight - 40));

  return {top, left};
};

export const FloatingCommentButton = ({
  insertComment
}: FloatingCommentButtonProps): React.JSX.Element | null => {
  const selection = useSlateSelector((ed) => ed.selection);
  const [pos, setPos] = useState<{top: number; left: number} | null>(null);

  const update = useCallback(() => {
    if (!selection || Range.isCollapsed(selection)) {
      setPos(null);

      return;
    }
    try {
      setPos(computeButtonPosition());
    } catch {
      setPos(null);
    }
  }, [selection]);

  useLayoutEffect(() => {
    update();
    const eP = document.querySelector('.editor-panel');
    if (eP instanceof HTMLElement) {
      eP.addEventListener('scroll', update, {passive: true});
    }
    window.addEventListener('resize', update);

    return () => {
      if (eP instanceof HTMLElement) {
        eP.removeEventListener('scroll', update);
      }
      window.removeEventListener('resize', update);
    };
  }, [update]);

  if (!pos) return null;

  return (
    <div
      className="floating-comment-button"
      contentEditable={false}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        zIndex: 1000,
        pointerEvents: 'auto'
      }}
    >
      <button
        className="toolbar-btn floating-add-comment"
        onMouseDown={(e) => {
          e.preventDefault();
          insertComment();
        }}
        title="Add Comment"
        aria-label="Add comment"
        style={{
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '50%',
          width: '32px',
          height: '32px',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
          cursor: 'pointer'
        }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
          <path
            // eslint-disable-next-line max-len
            d="M21.99 4c0-1.1-.89-2-1.99-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18zM20 4v13.17L18.83 16H4V4h16z"
            fill="#5f6368"
          />
          <circle cx="17" cy="17" r="6" fill="#fff" />
          <circle cx="17" cy="17" r="5" fill="#5ba85a" />
          <path d="M17 14.5v5M14.5 17h5" stroke="white" strokeWidth="1.5" />
        </svg>
      </button>
    </div>
  );
};
